import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { get, post } from "../../lib/http";
import { API } from "../../config/api";
import { toast } from "react-toastify";
import { rememberActiveEventId, resolveLockedEventId, offlineBookingsPath } from "../../organizer/eventContext.js";

const cn = (...a) => a.filter(Boolean).join(" ");
const fmtInr = (n = 0) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const CASH_TICKET_CAP = 6;
const uuid = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

function sessionUser() {
  try {
    return JSON.parse(localStorage.getItem("bb_user") || "null");
  } catch {
    return null;
  }
}

function Text({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-slate-700">{label}</span>
      <input
        {...props}
        className={cn(
          "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-200/80 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-70",
          props.className
        )}
      />
    </label>
  );
}

function Select({ label, children, ...props }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-slate-700">{label}</span>
      <select
        {...props}
        className="w-full cursor-pointer appearance-none rounded-xl border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm text-slate-900 shadow-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-200/80 disabled:cursor-not-allowed disabled:bg-slate-50"
      >
        {children}
      </select>
    </label>
  );
}

function Chip({ tone = "slate", children }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 ring-slate-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    red: "bg-rose-50 text-rose-700 ring-rose-200",
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${tones[tone]}`}>
      {children}
    </span>
  );
}

function SuccessModal({ open, onClose, onNew, onView, summary, complimentary }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center">
      <div onClick={onClose} className="absolute inset-0 bg-black/40" />
      <div className="relative m-0 w-full rounded-2xl bg-white shadow-xl sm:m-4 sm:w-[520px]">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-slate-900">
                Offline tickets issued!
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Tickets have been generated
                {summary?.buyer?.email ? <> and emailed to <b>{summary.buyer.email}</b></> : ""}.
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border p-3">
              <div className="text-slate-500">Event</div>
              <div className="truncate font-semibold text-slate-900">{summary?.eventName || "-"}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-slate-500">Booking Code</div>
              <div className="truncate font-semibold text-slate-900">
                {summary?.bookingCode || summary?.bookingId || "-"}
              </div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-slate-500">Tickets</div>
              <div className="font-semibold text-slate-900">{summary?.totalTickets ?? 0}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-slate-500">Amount</div>
              <div className="font-semibold text-slate-900">
                Free
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row">
            <button
              onClick={onNew}
              className="inline-flex flex-1 justify-center rounded-xl border px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Issue another
            </button>
            <button
              onClick={onView}
              className="inline-flex flex-1 justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
            >
              View booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OrganizerCashBooking() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const lockedEventId = resolveLockedEventId({
    pathname: location.pathname,
    state: location.state,
    searchParams,
    params,
  });

  const me = sessionUser();

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(lockedEventId);
  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const complimentary = true;

  const [buyer, setBuyer] = useState({ name: "", email: "", mobile: "" });
  const [assignee, setAssignee] = useState({
    name: me?.name || "",
    email: me?.email || "",
    mobile: String(me?.mobile || me?.phone || "").replace(/\D/g, "").slice(0, 10),
  });
  const [lines, setLines] = useState([
    { categoryId: "", price: "", quantity: 1, hintSeats: null, userPriceOverride: false },
  ]);
  const [discount, setDiscount] = useState({ code: "", mode: "flat", value: "" });
  const [notes, setNotes] = useState("");
  const [idKey, setIdKey] = useState(uuid());
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successSummary, setSuccessSummary] = useState(null);

  const cashCap = Math.max(1, Number(event?.maxTicketsPerUser) || CASH_TICKET_CAP);
  const totalQty = lines.reduce((s, l) => s + Number(l.quantity || 0), 0);

  const listPath = offlineBookingsPath(lockedEventId);
  const dashboardPath = lockedEventId ? `/events/dashboard/${lockedEventId}` : "/listyourshow/manage";

  useEffect(() => {
    if (lockedEventId && eventId !== lockedEventId) setEventId(lockedEventId);
  }, [lockedEventId, eventId]);

  useEffect(() => {
    if (lockedEventId) return undefined;
    let on = true;
    (async () => {
      try {
        const data = await get(`${API.events.mySubmissions}?approvalStatus=approved&limit=100`);
        const arr = data?.events || data || [];
        if (!on) return;
        setEvents(Array.isArray(arr) ? arr : []);
      } catch {
        if (on) setEvents([]);
      }
    })();
    return () => {
      on = false;
    };
  }, [lockedEventId]);

  useEffect(() => {
    if (!eventId) {
      setEvent(null);
      return undefined;
    }
    let on = true;
    (async () => {
      try {
        setLoadingEvent(true);
        const data = await get(`${API.events.getSinglEvent}/${eventId}`);
        if (!on) return;
        const ev = data?.event || data || null;
        setEvent(ev);
        const first = ev?.ticketCategories?.[0];
        setLines([
          {
            categoryId: first?.type || "",
            price: complimentary ? 0 : first?.price || "",
            quantity: 1,
            hintSeats: first?.availableSeats ?? null,
            userPriceOverride: false,
          },
        ]);
      } catch {
        if (on) setEvent(null);
      } finally {
        if (on) setLoadingEvent(false);
      }
    })();
    return () => {
      on = false;
    };
    // complimentary is applied in a separate effect so category fetch isn't retriggered by toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const categories = useMemo(() => event?.ticketCategories || [], [event]);

  useEffect(() => {
    setLines((prev) => {
      let remaining = complimentary ? Infinity : cashCap;
      return prev.map((l) => {
        const evCat = categories.find((c) => c.type === l.categoryId);
        let qty = Math.max(1, Number(l.quantity || 1));
        if (!complimentary) {
          qty = Math.min(qty, remaining);
          remaining = Math.max(0, remaining - qty);
        }
        return {
          ...l,
          price: complimentary ? 0 : evCat?.price ?? l.price,
          quantity: qty,
          userPriceOverride: false,
        };
      });
    });
    if (complimentary) setDiscount({ code: "", mode: "flat", value: "" });
  }, [complimentary, categories, cashCap]);

  const updateLine = (i, patch) => {
    setLines((prev) => {
      const next = [...prev];
      const merged = { ...next[i], ...patch };
      if ("categoryId" in patch) {
        const evCat = categories.find((c) => c.type === patch.categoryId);
        merged.price = complimentary ? 0 : evCat?.price ?? "";
        merged.hintSeats = evCat?.availableSeats ?? null;
        merged.userPriceOverride = false;
      }
      if ("price" in patch) merged.userPriceOverride = true;
      if ("quantity" in patch) {
        const others = prev.reduce(
          (s, l, idx) => (idx === i ? s : s + Number(l.quantity || 0)),
          0
        );
        const seatLeft = merged.hintSeats;
        let qty = Math.max(1, Number(merged.quantity || 1));
        if (!complimentary) {
          qty = Math.min(qty, Math.max(1, cashCap - others));
        }
        if (seatLeft != null) qty = Math.min(qty, Math.max(1, Number(seatLeft)));
        merged.quantity = qty;
      }
      next[i] = merged;
      return next;
    });
  };

  const addLine = () => {
    if (!complimentary && totalQty >= cashCap) return;
    const used = new Set(lines.map((l) => l.categoryId).filter(Boolean));
    const nextCat = categories.find((c) => !used.has(c.type)) || categories[0];
    setLines((prev) => [
      ...prev,
      {
        categoryId: nextCat?.type || "",
        price: complimentary ? 0 : nextCat?.price || "",
        quantity: 1,
        hintSeats: nextCat?.availableSeats ?? null,
        userPriceOverride: false,
      },
    ]);
  };

  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const subtotal = useMemo(
    () =>
      complimentary
        ? 0
        : lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.price || 0), 0),
    [lines, complimentary]
  );
  const discountRaw = Number(discount.value || 0);
  const percent = discount.mode === "percent" ? Math.max(0, Math.min(100, discountRaw)) : 0;
  const discountValue = complimentary
    ? 0
    : discount.mode === "percent"
      ? Math.round((subtotal * percent) / 100)
      : Math.max(0, Math.min(subtotal, Number(discount.value || 0)));
  const total = Math.max(0, subtotal - discountValue);

  const valid =
    eventId &&
    lines.length > 0 &&
    lines.every((l) => l.categoryId && Number(l.quantity) > 0 && Number(l.price) >= 0) &&
    (complimentary || totalQty <= cashCap) &&
    ((buyer.email && /\S+@\S+\.\S+/.test(buyer.email)) ||
      (buyer.mobile && /^\d{10}$/.test(buyer.mobile)));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please complete required fields properly.");
      return;
    }
    if (!complimentary && totalQty > cashCap) {
      toast.error(`Cash booking allows maximum ${cashCap} tickets.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        user: { name: buyer.name, email: buyer.email, mobile: buyer.mobile },
        assignee:
          assignee?.name || assignee?.email || assignee?.mobile
            ? { name: assignee.name, email: assignee.email, mobile: assignee.mobile }
            : undefined,
        eventId,
        categories: lines.map((l) => ({
          categoryId: l.categoryId,
          quantity: Number(l.quantity || 0),
          price: complimentary ? 0 : Number(l.price || 0),
        })),
        discount:
          !complimentary && discountValue > 0
            ? {
                code: discount.code || undefined,
                value: discountValue,
                meta: discount.mode === "percent" ? { mode: "percent", raw: discountRaw } : undefined,
              }
            : undefined,
        notes: notes || undefined,
        complimentary,
        issueType: "offline",
        idempotencyKey: idKey,
      };

      const resp = await post(API.Payment.createCashBooking, payload);
      if (resp?.success) {
        toast.success(
          resp.idempotent ? "Already issued" : "Offline tickets issued"
        );
        const booking = resp.booking || {};
        const tickets = resp.tickets || {};
        const eventName =
          booking?.eventId?.name ||
          event?.name ||
          events.find((ev) => (ev._id || ev.id) === eventId)?.name ||
          "";
        const totalTickets =
          (tickets?.ticketCategories || []).reduce(
            (sum, c) => sum + Number(c.finalQuantity || 0),
            0
          ) || lines.reduce((s, l) => s + Number(l.quantity || 0), 0);

        setSuccessSummary({
          eventName,
          bookingCode: booking?.bookingCode,
          bookingId: booking?._id,
          amount: resp?.payment?.amount ?? resp?.payment?.finalAmount ?? booking?.finalAmount ?? 0,
          buyer: { name: buyer.name, email: buyer.email, mobile: buyer.mobile },
          totalTickets,
        });
        setSuccessOpen(true);
      } else {
        throw new Error(resp?.message || "Failed to issue tickets");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
      setIdKey(uuid());
    }
  };

  const resetForm = () => {
    setBuyer({ name: "", email: "", mobile: "" });
    setNotes("");
    setDiscount({ code: "", mode: "flat", value: "" });
    const first = event?.ticketCategories?.[0];
    setLines([
      {
        categoryId: first?.type || "",
        price: first?.price || "",
        quantity: 1,
        hintSeats: first?.availableSeats ?? null,
        userPriceOverride: false,
      },
    ]);
    if (!lockedEventId) {
      setEventId("");
      setEvent(null);
    }
  };

  if (
    lockedEventId &&
    location.pathname.startsWith("/listyourshow/cash-booking") &&
    !location.pathname.startsWith("/listyourshow/cash-bookings")
  ) {
    return (
      <Navigate
        to={`/events/dashboard/${lockedEventId}/cash-booking`}
        replace
        state={{ eventId: lockedEventId }}
      />
    );
  }

  if (!lockedEventId) {
    return <Navigate to="/listyourshow/manage" replace />;
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase text-amber-700">Issue tickets</div>
          <h1 className="text-xl font-extrabold text-slate-900 sm:text-2xl">Offline booking</h1>
          <p className="mt-0.5 text-sm text-slate-600">
            {event?.name
              ? `Issuing offline tickets for ${event.name}.`
              : "Issue offline tickets for your event."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={listPath}
            className="rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
          >
            Offline bookings
          </Link>
          <Link
            to={dashboardPath}
            className="rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
          >
            ← Back
          </Link>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-base font-semibold text-slate-900">Issue type</h2>
            <div className="rounded-xl border border-emerald-400 bg-emerald-50 px-4 py-3 text-left">
              <div className="text-sm font-semibold text-slate-900">Offline mode</div>
              <div className="mt-0.5 text-xs font-normal text-slate-500">
                Issue tickets offline — no online payment.
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">Event</h2>
              {loadingEvent && <span className="text-xs text-slate-500">loading…</span>}
            </div>
            {lockedEventId ? (
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  {event?.name || "Loading event…"}
                </div>
                <p className="mt-1 text-xs text-slate-500">Using the event from this dashboard — no need to choose again.</p>
              </div>
            ) : null}
            {event && (
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                {event.date && <Chip>{new Date(event.date).toLocaleString()}</Chip>}
                {event.venue?.name && <Chip tone="green">{event.venue.name}</Chip>}
                {event.venue?.city && <Chip tone="blue">{event.venue.city}</Chip>}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-base font-semibold text-slate-900">Guest / buyer</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <Text
                label="Full name"
                placeholder="e.g. Rohan Singh"
                value={buyer.name}
                onChange={(e) => setBuyer((b) => ({ ...b, name: e.target.value }))}
              />
              <Text
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={buyer.email}
                onChange={(e) => setBuyer((b) => ({ ...b, email: e.target.value }))}
              />
              <Text
                label="Mobile (10-digit)"
                inputMode="numeric"
                maxLength={10}
                placeholder="98XXXXXXXX"
                value={buyer.mobile}
                onChange={(e) =>
                  setBuyer((b) => ({ ...b, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))
                }
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">Need at least email or mobile to deliver tickets.</p>
          </div>

          <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-base font-semibold text-slate-900">Issued by</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <Text
                label="Name"
                value={assignee.name}
                onChange={(e) => setAssignee((a) => ({ ...a, name: e.target.value }))}
              />
              <Text
                label="Email (optional)"
                type="email"
                value={assignee.email}
                onChange={(e) => setAssignee((a) => ({ ...a, email: e.target.value }))}
              />
              <Text
                label="Mobile (optional)"
                inputMode="numeric"
                maxLength={10}
                value={assignee.mobile}
                onChange={(e) =>
                  setAssignee((a) => ({ ...a, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))
                }
              />
            </div>
          </div>

          <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">Ticket categories</h2>
              <button
                type="button"
                onClick={addLine}
                disabled={!categories.length || (!complimentary && totalQty >= cashCap)}
                className="rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-amber-50 disabled:opacity-50"
              >
                Add category
              </button>
            </div>
            <p className="mb-3 text-xs text-slate-500">
              {complimentary
                ? "Offline tickets have no quantity cap (limited by seats left)."
                : `Cash booking: maximum ${cashCap} ticket${cashCap === 1 ? "" : "s"} in one booking.`}
            </p>
            <div className="space-y-3">
              {lines.map((l, i) => {
                const evCat = categories.find((c) => c.type === l.categoryId);
                const seatLeft = evCat?.availableSeats ?? l.hintSeats;
                const others = totalQty - Number(l.quantity || 0);
                const capLeft = complimentary ? Infinity : Math.max(1, cashCap - others);
                const maxQty = Math.min(
                  seatLeft != null ? Number(seatLeft) : capLeft,
                  capLeft
                );
                const over = seatLeft != null && Number(l.quantity || 0) > Number(seatLeft);
                return (
                  <div key={i} className="rounded-xl border p-4">
                    <div className="grid gap-3 sm:grid-cols-[1.3fr_1fr_1fr_auto] sm:items-end">
                      <Select
                        label="Category"
                        value={l.categoryId}
                        onChange={(e) => updateLine(i, { categoryId: e.target.value })}
                      >
                        <option value="">— choose —</option>
                        {categories.map((c) => (
                          <option key={c.type} value={c.type}>
                            {c.type} {c.price ? `• ${fmtInr(c.price)}` : ""}
                          </option>
                        ))}
                      </Select>
                      <Text
                        label="Price (each)"
                        type="number"
                        min="0"
                        value={complimentary ? 0 : l.price}
                        disabled={complimentary}
                        onChange={(e) => updateLine(i, { price: e.target.value })}
                      />
                      <Text
                        label="Quantity"
                        type="number"
                        min="1"
                        max={Number.isFinite(maxQty) ? maxQty : undefined}
                        value={l.quantity}
                        onChange={(e) => updateLine(i, { quantity: e.target.value })}
                      />
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-900">
                          {fmtInr(
                            complimentary ? 0 : Number(l.price || 0) * Number(l.quantity || 0)
                          )}
                        </div>
                        {lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLine(i)}
                            className="rounded-full border px-3 py-1.5 text-xs text-rose-600 hover:bg-rose-50"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <div className="space-x-2">
                        {seatLeft != null && (
                          <Chip tone={over ? "red" : "slate"}>Seats left: {seatLeft}</Chip>
                        )}
                        {complimentary && <Chip tone="green">offline</Chip>}
                      </div>
                      {over && (
                        <div className="font-medium text-rose-600">Insufficient seats in this category</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {!complimentary && (
            <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-base font-semibold text-slate-900">Discount & notes</h2>
              <div className="grid gap-4 sm:grid-cols-4">
                <Text
                  label="Discount code (optional)"
                  value={discount.code}
                  onChange={(e) => setDiscount((d) => ({ ...d, code: e.target.value }))}
                />
                <Select
                  label="Mode"
                  value={discount.mode}
                  onChange={(e) => setDiscount((d) => ({ ...d, mode: e.target.value }))}
                >
                  <option value="flat">Flat (₹)</option>
                  <option value="percent">Percent (%)</option>
                </Select>
                <Text
                  label={discount.mode === "percent" ? "Discount (%)" : "Discount amount (₹)"}
                  type="number"
                  min="0"
                  value={discount.value}
                  onChange={(e) => setDiscount((d) => ({ ...d, value: e.target.value }))}
                />
                <Text label="Notes (internal)" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
          )}

          {complimentary && (
            <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
              <Text
                label="Notes (internal)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Artist guest, media pass"
              />
            </div>
          )}
        </div>

        <aside className="space-y-6 lg:sticky lg:top-4 lg:self-start">
          <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-base font-semibold text-slate-900">Review</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Type</span>
                <span className="font-semibold text-slate-900">
                  {complimentary ? "Offline mode" : "Cash"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-semibold text-slate-900">{fmtInr(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Discount</span>
                <span className="font-semibold text-emerald-700">− {fmtInr(discountValue)}</span>
              </div>
              <div className="flex items-center justify-between border-t pt-3">
                <span className="font-semibold text-slate-900">Total</span>
                <span className="text-lg font-bold text-slate-900">
                  {complimentary ? "Free" : fmtInr(total)}
                </span>
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
              <ul className="list-disc space-y-1 pl-4">
                <li>
                  {complimentary
                    ? "Recorded as an offline (₹0) booking."
                    : "Payment method recorded as cash."}
                </li>
                <li>Tickets & PDF emailed if buyer email is provided.</li>
              </ul>
            </div>
            <button
              type="submit"
              disabled={!valid || submitting}
              className={cn(
                "mt-4 w-full rounded-xl bg-amber-400 px-4 py-2.5 text-sm font-bold text-slate-900 shadow-sm hover:bg-amber-300",
                (!valid || submitting) && "cursor-not-allowed opacity-50"
              )}
            >
              {submitting ? "Issuing…" : "Issue offline tickets"}
            </button>
          </div>

          {event && (
            <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
              <h3 className="mb-3 text-base font-semibold text-slate-900">Availability</h3>
              <div className="space-y-2">
                {event.ticketCategories?.map((c) => (
                  <div key={c.type} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{c.type}</span>
                    <div className="flex items-center gap-2">
                      <Chip tone="green">{fmtInr(c.price)}</Chip>
                      <Chip tone={(c.availableSeats ?? 0) > 0 ? "slate" : "red"}>
                        {c.availableSeats ?? 0} left
                      </Chip>
                    </div>
                  </div>
                ))}
                {!event.ticketCategories?.length && (
                  <div className="text-sm text-slate-500">No ticket categories configured.</div>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      <SuccessModal
        open={successOpen}
        summary={successSummary}
        complimentary={complimentary}
        onClose={() => setSuccessOpen(false)}
        onNew={() => {
          setSuccessOpen(false);
          resetForm();
        }}
        onView={() => {
          setSuccessOpen(false);
          if (successSummary?.bookingId) {
            navigate(`/listyourshow/cash-bookings/${successSummary.bookingId}`, { replace: true });
          } else {
            navigate(listPath, { replace: true });
          }
        }}
      />
    </form>
  );
}
